/**
 * Los textos de la pagina que edita el dueno del producto.
 *
 * Que es esto
 * -----------
 * El dueno del producto tiene una consola —en otro origen, detras de un segundo
 * factor— donde edita cinco textos que se le muestran a los usuarios de TODAS
 * las organizaciones. Este archivo es el lado del cliente de ese circuito: lo
 * unico que los trae. Antes no existia, y esa era exactamente la falla: los
 * textos se guardaban, la consola contestaba «Guardado.» y no los leia nadie.
 *
 * Tres decisiones que conviene conocer antes de tocar este archivo:
 *
 * 1. **No pasa por `lib/api.ts`.** Ese cliente adjunta el token, renueva la
 *    sesion y expulsa al ingreso cuando el backend contesta 401. Nada de eso
 *    aplica: este endpoint es publico porque la pantalla de ingreso lo necesita
 *    ANTES de que exista un token. Usar el cliente con sesion aca ataria el
 *    nombre del producto a estar logueado.
 *
 * 2. **Nunca falla hacia afuera.** Si el pedido se cae, tarda o devuelve
 *    cualquier cosa rara, se usan los valores de fabrica. Un texto de marca no
 *    puede ser el motivo por el que alguien no pueda entrar a trabajar, y una
 *    pantalla de ingreso que se queda en blanco porque no cargo un subtitulo
 *    seria un desastre de una magnitud que el subtitulo no justifica.
 *
 * 3. **Lo que llega es TEXTO, y se renderiza como texto.** El backend rechaza al
 *    escribir los signos `<`, `>`, `javascript:`, `data:` y los caracteres de
 *    control; aca no se hace `dangerouslySetInnerHTML` en ningun lado. Las dos
 *    mitades juntas son lo que separa este campo de un XSS almacenado contra
 *    todos los clientes a la vez, disparado por el propio dueno. Si alguna vez
 *    alguien necesita negrita aca, la respuesta es no.
 */

/** Las cinco claves editables. Es la misma lista cerrada que el backend. */
export type ClaveDeMarca =
  | 'nombre_comercial'
  | 'leyenda_ingreso'
  | 'aviso_mantenimiento'
  | 'pie'
  | 'contacto_soporte';

export type Marca = Record<ClaveDeMarca, string>;

/**
 * Los textos originales.
 *
 * Estan duplicados a proposito respecto del backend (`nucleo/apps/dueno/textos.py`).
 * No es un descuido: son el valor que se usa cuando el backend NO contesta, asi
 * que derivarlos de el seria pedirle al que no responde que diga que decir.
 */
export const TEXTOS_DE_FABRICA: Marca = {
  nombre_comercial: 'Núcleo',
  leyenda_ingreso: 'Sistema de gestión para administradoras de consorcios.',
  aviso_mantenimiento: '',
  pie: '',
  contacto_soporte: '',
};

const RUTA = '/api/v1/marca/';

/** Cuanto se espera antes de rendirse y dibujar los valores de fabrica. */
const TOPE_MS = 4000;

/**
 * Trae los textos. **Nunca rechaza.**
 *
 * El `AbortController` no es decorativo: sin tope, un backend que acepta la
 * conexion y no contesta deja la marca en «cargando» para siempre. Cuatro
 * segundos es mas de lo que tarda cualquier respuesta sana y bastante menos de
 * lo que una persona espera mirando una pantalla.
 */
export async function leerMarca(): Promise<Marca> {
  const corte = new AbortController();
  const reloj = setTimeout(() => {
    corte.abort();
  }, TOPE_MS);

  try {
    const respuesta = await fetch(RUTA, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: corte.signal,
    });
    if (!respuesta.ok) return TEXTOS_DE_FABRICA;
    return normalizar(await respuesta.json());
  } catch {
    // Red caida, CORS, JSON roto, tiempo agotado: todos terminan igual, y a
    // proposito. Distinguirlos solo serviria para escribir un mensaje de error
    // sobre un subtitulo, y no hay ninguna accion que la persona pueda tomar.
    return TEXTOS_DE_FABRICA;
  } finally {
    clearTimeout(reloj);
  }
}

/**
 * Se queda con las claves conocidas y descarta el resto.
 *
 * Es la misma lista blanca que aplica el backend, repetida de este lado. Que las
 * dos puntas la apliquen significa que una clave nueva no llega a la pantalla
 * hasta que alguien la agregue aca **y** decida donde se dibuja: no hay forma de
 * que aparezca sola en un lugar que nadie eligio.
 */
function normalizar(crudo: unknown): Marca {
  if (crudo === null || typeof crudo !== 'object') return TEXTOS_DE_FABRICA;
  const entrada = crudo as Record<string, unknown>;
  const salida = { ...TEXTOS_DE_FABRICA };
  for (const clave of Object.keys(TEXTOS_DE_FABRICA) as ClaveDeMarca[]) {
    const valor = entrada[clave];
    if (typeof valor === 'string') salida[clave] = valor;
  }
  return salida;
}
